/**
 * Dynamic Client Storage Management System
 * Handles saving, retrieving, and managing client analysis data
 */

// Configuration for client storage
const CLIENT_STORAGE_CONFIG = {
  storageKey: process.env.REACT_APP_CLIENT_STORAGE_KEY || 'hairchitech_clients',
  maxClients: parseInt(process.env.REACT_APP_MAX_CLIENTS || '20', 10), // Further reduced
  imageQuality: parseFloat(process.env.REACT_APP_IMAGE_QUALITY || '0.4'), // Much more aggressive compression
  imageFormat: process.env.REACT_APP_IMAGE_FORMAT || 'image/jpeg',
  autoCleanup: process.env.REACT_APP_AUTO_CLEANUP === 'true',
  cleanupDays: parseInt(process.env.REACT_APP_CLEANUP_DAYS || '3', 10), // Much shorter cleanup
  maxImageWidth: parseInt(process.env.REACT_APP_MAX_IMAGE_WIDTH || '200', 10), // Much smaller
  maxImageHeight: parseInt(process.env.REACT_APP_MAX_IMAGE_HEIGHT || '150', 10), // Much smaller
  enableImageStorage: process.env.REACT_APP_ENABLE_IMAGE_STORAGE !== 'false', // Can be disabled
  enableGeneratedImageStorage: process.env.REACT_APP_ENABLE_GENERATED_IMAGE_STORAGE === 'true', // Disabled by default
};

/**
 * Generate unique client ID
 */
export const generateClientId = () => {
  return `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Convert image file to base64 for storage
 */
export const imageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Compress image for storage
 */
export const compressImage = (file, quality = CLIENT_STORAGE_CONFIG.imageQuality) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      const maxWidth = CLIENT_STORAGE_CONFIG.maxImageWidth;
      const maxHeight = CLIENT_STORAGE_CONFIG.maxImageHeight;
      
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          const compressedFile = new File([blob], file.name, {
            type: CLIENT_STORAGE_CONFIG.imageFormat,
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        CLIENT_STORAGE_CONFIG.imageFormat,
        quality
      );
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Check localStorage quota and estimate usage
 */
export const checkStorageQuota = () => {
  try {
    const testKey = 'quota_test';
    const testData = 'x'.repeat(1024 * 1024); // 1MB test
    
    localStorage.setItem(testKey, testData);
    localStorage.removeItem(testKey);
    
    return { available: true, estimated: 'Unknown' };
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      return { available: false, estimated: 'Full' };
    }
    return { available: true, estimated: 'Unknown' };
  }
};

/**
 * Get estimated storage usage
 */
export const getStorageUsage = () => {
  try {
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length;
      }
    }
    return {
      used: totalSize,
      usedMB: (totalSize / (1024 * 1024)).toFixed(2),
      available: true
    };
  } catch (error) {
    return { used: 0, usedMB: '0', available: false };
  }
};

/**
 * Download and convert image URL to base64
 */
export const downloadImageAsBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error downloading image:', error);
    throw new Error('Failed to download generated hairstyle image');
  }
};

/**
 * Save client analysis data
 */
export const saveClientAnalysis = async (clientData) => {
  try {
    // Check storage quota before proceeding
    const quotaCheck = checkStorageQuota();
    if (!quotaCheck.available) {
      console.warn('Storage quota exceeded, cleaning up old clients...');
      cleanupOldClients();
      // Try again after cleanup
      const retryQuotaCheck = checkStorageQuota();
      if (!retryQuotaCheck.available) {
        throw new Error('Storage quota exceeded. Please clear browser data or reduce image quality.');
      }
    }

    const clients = getClients();
    
    // Generate unique ID
    const clientId = generateClientId();
    
    // Compress and convert original image
    const compressedImage = await compressImage(clientData.imageFile);
    const imageBase64 = await imageToBase64(compressedImage);
    
    // Download and convert generated hairstyle image if available and enabled
    let generatedHairstyleBase64 = null;
    if (CLIENT_STORAGE_CONFIG.enableImageStorage && CLIENT_STORAGE_CONFIG.enableGeneratedImageStorage && clientData.result?.output) {
      try {
        generatedHairstyleBase64 = await downloadImageAsBase64(clientData.result.output);
      } catch (error) {
        console.warn('Could not download generated hairstyle image:', error);
        // Continue without the generated image
      }
    }
    
    // Create client record
    const clientRecord = {
      id: clientId,
      name: clientData.name,
      phone: clientData.phone || '',
      email: clientData.email || '',
      image: imageBase64,
      imageName: clientData.imageFile.name,
      imageSize: compressedImage.size,
      analysisData: {
        shape: clientData.shapeData?.shape,
        metrics: clientData.shapeData?.metrics,
        prompts: clientData.shapeData?.prompts,
        analysisTime: clientData.analysisTime,
        confidence: clientData.confidence || 'High',
      },
      generationData: clientData.result ? {
        status: clientData.result.status,
        outputUrl: clientData.result.output,
        outputImage: generatedHairstyleBase64, // Store the actual image data
        promptUsed: clientData.result.promptUsed,
        orderId: clientData.result.orderId,
        generationTime: clientData.generationTime,
        styleIndex: clientData.selectedStyleIndex,
        customPrompt: clientData.customPrompt,
      } : null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    // Add to clients array
    clients.unshift(clientRecord); // Add to beginning for newest first
    
    // Limit number of clients
    if (clients.length > CLIENT_STORAGE_CONFIG.maxClients) {
      clients.splice(CLIENT_STORAGE_CONFIG.maxClients);
    }
    
    // Try to save to localStorage with aggressive error handling
    try {
      localStorage.setItem(CLIENT_STORAGE_CONFIG.storageKey, JSON.stringify(clients));
    } catch (storageError) {
      if (storageError.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded, attempting aggressive cleanup...');
        
        // Try multiple cleanup strategies
        let success = false;
        let attempts = 0;
        const maxAttempts = 3;
        
        while (!success && attempts < maxAttempts) {
          attempts++;
          
          // Strategy 1: Keep only 50% of clients
          let reducedClients = clients.slice(0, Math.floor(clients.length * 0.5));
          
          // Strategy 2: If still failing, keep only 25%
          if (attempts === 2) {
            reducedClients = clients.slice(0, Math.floor(clients.length * 0.25));
          }
          
          // Strategy 3: If still failing, keep only 10%
          if (attempts === 3) {
            reducedClients = clients.slice(0, Math.floor(clients.length * 0.1));
          }
          
          try {
            localStorage.setItem(CLIENT_STORAGE_CONFIG.storageKey, JSON.stringify(reducedClients));
            console.log(`Reduced clients from ${clients.length} to ${reducedClients.length} due to storage quota (attempt ${attempts})`);
            success = true;
          } catch (retryError) {
            console.warn(`Cleanup attempt ${attempts} failed, trying more aggressive cleanup...`);
          }
        }
        
        if (!success) {
          throw new Error('Storage quota exceeded. Please clear browser data completely.');
        }
      } else {
        throw storageError;
      }
    }
    
    // Auto cleanup if enabled
    if (CLIENT_STORAGE_CONFIG.autoCleanup) {
      cleanupOldClients();
    }
    
    return clientRecord;
  } catch (error) {
    console.error('Error saving client analysis:', error);
    if (error.message.includes('quota')) {
      throw new Error('Storage quota exceeded. Please clear browser data or reduce image quality.');
    }
    throw new Error('Failed to save client analysis');
  }
};

/**
 * Get all clients
 */
export const getClients = () => {
  try {
    const clientsData = localStorage.getItem(CLIENT_STORAGE_CONFIG.storageKey);
    return clientsData ? JSON.parse(clientsData) : [];
  } catch (error) {
    console.error('Error retrieving clients:', error);
    return [];
  }
};

/**
 * Get client by ID
 */
export const getClientById = (clientId) => {
  const clients = getClients();
  return clients.find(client => client.id === clientId);
};

/**
 * Update client data
 */
export const updateClient = (clientId, updates) => {
  try {
    const clients = getClients();
    const clientIndex = clients.findIndex(client => client.id === clientId);
    
    if (clientIndex !== -1) {
      clients[clientIndex] = {
        ...clients[clientIndex],
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      
      localStorage.setItem(CLIENT_STORAGE_CONFIG.storageKey, JSON.stringify(clients));
      return clients[clientIndex];
    }
    
    return null;
  } catch (error) {
    console.error('Error updating client:', error);
    throw new Error('Failed to update client');
  }
};

/**
 * Delete client
 */
export const deleteClient = (clientId) => {
  try {
    const clients = getClients();
    const filteredClients = clients.filter(client => client.id !== clientId);
    localStorage.setItem(CLIENT_STORAGE_CONFIG.storageKey, JSON.stringify(filteredClients));
    return true;
  } catch (error) {
    console.error('Error deleting client:', error);
    throw new Error('Failed to delete client');
  }
};

/**
 * Clean up old clients
 */
export const cleanupOldClients = () => {
  try {
    const clients = getClients();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - CLIENT_STORAGE_CONFIG.cleanupDays);
    
    const recentClients = clients.filter(client => 
      new Date(client.createdAt) > cutoffDate
    );
    
    if (recentClients.length !== clients.length) {
      localStorage.setItem(CLIENT_STORAGE_CONFIG.storageKey, JSON.stringify(recentClients));
      console.log(`Cleaned up ${clients.length - recentClients.length} old clients`);
    }
  } catch (error) {
    console.error('Error cleaning up clients:', error);
  }
};

/**
 * Clear all client data (emergency cleanup)
 */
export const clearAllClientData = () => {
  try {
    localStorage.removeItem(CLIENT_STORAGE_CONFIG.storageKey);
    console.log('All client data cleared');
    return true;
  } catch (error) {
    console.error('Error clearing all client data:', error);
    return false;
  }
};

/**
 * Get client statistics
 */
export const getClientStats = () => {
  const clients = getClients();
  const stats = {
    total: clients.length,
    withAnalysis: clients.filter(c => c.analysisData).length,
    withGeneration: clients.filter(c => c.generationData).length,
    shapes: {},
    recentClients: clients.filter(c => {
      const daysSince = (new Date() - new Date(c.createdAt)) / (1000 * 60 * 60 * 24);
      return daysSince <= 7;
    }).length,
  };
  
  // Count shapes
  clients.forEach(client => {
    if (client.analysisData?.shape) {
      stats.shapes[client.analysisData.shape] = (stats.shapes[client.analysisData.shape] || 0) + 1;
    }
  });
  
  return stats;
};

/**
 * Export clients data
 */
export const exportClientsData = () => {
  const clients = getClients();
  const exportData = {
    exportDate: new Date().toISOString(),
    totalClients: clients.length,
    clients: clients.map(client => ({
      ...client,
      image: undefined, // Remove base64 images for export
      imageBase64: client.image, // Keep image data separate
    })),
  };
  
  return exportData;
};

/**
 * Import clients data
 */
export const importClientsData = (importData) => {
  try {
    if (!importData.clients || !Array.isArray(importData.clients)) {
      throw new Error('Invalid import data format');
    }
    
    const existingClients = getClients();
    const mergedClients = [...importData.clients, ...existingClients];
    
    // Remove duplicates based on ID
    const uniqueClients = mergedClients.filter((client, index, self) =>
      index === self.findIndex(c => c.id === client.id)
    );
    
    localStorage.setItem(CLIENT_STORAGE_CONFIG.storageKey, JSON.stringify(uniqueClients));
    return uniqueClients.length;
  } catch (error) {
    console.error('Error importing clients:', error);
    throw new Error('Failed to import clients data');
  }
};

const clientStorage = {
  generateClientId,
  imageToBase64,
  compressImage,
  downloadImageAsBase64,
  checkStorageQuota,
  getStorageUsage,
  saveClientAnalysis,
  getClients,
  getClientById,
  updateClient,
  deleteClient,
  cleanupOldClients,
  clearAllClientData,
  getClientStats,
  exportClientsData,
  importClientsData,
  config: CLIENT_STORAGE_CONFIG,
};

export default clientStorage;
